import { Controller } from "@hotwired/stimulus"
import { loadPlan, savePlan, deepMerge } from "plan_storage"
import { renderIcons } from "lucide_icons"
import {
  bucketLoad, bucketAdd, bucketRemove, bucketClearResolved, bucketUpdateStatus, bucketSave,
  buildPrompt, parseReply, wordDiff
} from "proofread_bucket"

// Proofread Bucket — capture text selections for AI proofreading.
//
// Captures selected text from any editor field, marks it visually, persists to
// localStorage, and provides AI round-trip: copy prompt → paste reply → review
// options with word-level diff → apply or keep original.

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
}[c]))

// Section key → human-readable label map.
const SECTION_LABELS = {
  client: "Client",
  about: "About",
  circleOfSupport: "Circle of Support",
  strengths: "Strengths & Interests",
  dayInLife: "Day in Life",
  currentServices: "Current Services",
  unmetNeeds: "Unmet Needs",
  goals: "Goals",
  barriers: "Barriers",
  risks: "Risk Assessment",
  communication: "Communication Chart",
  review: "Review",
}

function sectionLabel(key) {
  return SECTION_LABELS[key] || key || "Unknown"
}

// Panel mode: "capture" (default list), "reply" (paste reply + review options)
const MODE_CAPTURE = "capture"
const MODE_REPLY = "reply"

export default class extends Controller {
  static targets = ["panel", "panelBody", "badge", "toggleBtn"]

  connect() {
    this._items = bucketLoad()
    this._highlightMap = new Map()   // id → { el, overlay }
    this._mode = MODE_CAPTURE
    this._parsed = null              // { [id]: { options: [] } } from parseReply
    this._onKeyDown = (e) => this._handleKeyDown(e)
    this._onPlanChange = () => this._reapplyHighlights()
    this._onContextMenu = (e) => this._handleContextMenu(e)
    document.addEventListener("keydown", this._onKeyDown)
    document.addEventListener("plan:change", this._onPlanChange)
    document.addEventListener("contextmenu", this._onContextMenu)
    this._reflectBadge()
    this._scheduleHighlights()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeyDown)
    document.removeEventListener("plan:change", this._onPlanChange)
    document.removeEventListener("contextmenu", this._onContextMenu)
    this._removeContextMenu()
    this._removeAllOverlays()
    if (this._highlightRaf) cancelAnimationFrame(this._highlightRaf)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Capture trigger
  // ─────────────────────────────────────────────────────────────────────────

  capture(event) {
    event?.preventDefault?.()
    const field = this._findField(window.getSelection())
    if (!field || !field.text) {
      this._toast("Select text in a plan field first.")
      return
    }
    this._captureField(field)
  }

  // Add a resolved field selection to the bucket. Shared by the toolbar button,
  // Ctrl+Shift+P, and the right-click "Add to Proofread bucket" menu.
  _captureField(field) {
    const { el, sectionKey, fieldPath, start, end, text } = field
    if (!text) return
    const item = bucketAdd({ sectionKey, fieldPath, start, end, original: text })
    this._items = bucketLoad()
    this._reflectBadge()
    this._applyHighlight(item, el)
    this._renderPanel()
    this._showPanel()
    this._toast(`Captured: "${text.slice(0, 30)}${text.length > 30 ? "…" : ""}"`)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Panel toggle
  // ─────────────────────────────────────────────────────────────────────────

  toggle(event) {
    event?.preventDefault?.()
    if (this.hasPanelTarget && !this.panelTarget.hidden) {
      this._hidePanel()
    } else {
      this._renderPanel()
      this._showPanel()
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Panel actions
  // ─────────────────────────────────────────────────────────────────────────

  remove(event) {
    const id = event.currentTarget.dataset.pbId
    if (!id) return
    bucketRemove(id)
    this._items = bucketLoad()
    this._removeOverlay(id)
    this._reflectBadge()
    this._renderPanel()
  }

  clearResolved(event) {
    event?.preventDefault?.()
    bucketClearResolved()
    this._items = bucketLoad()
    // Remove overlays for items that were cleared
    for (const [id] of this._highlightMap) {
      if (!this._items.find((it) => it.id === id)) {
        this._removeOverlay(id)
      }
    }
    this._reflectBadge()
    this._renderPanel()
  }

  // Copy prompt: builds instruction block + marked items, copies to clipboard
  copyPrompt(event) {
    event?.preventDefault?.()
    const openItems = this._items.filter(it => it.status === "open")
    const text = buildPrompt(openItems, loadPlan())
    if (!text) {
      this._toast("Nothing to copy.")
      return
    }
    navigator.clipboard.writeText(text).then(
      () => {
        this._toast("Copied — paste into your AI, then paste the reply below.")
        this._mode = MODE_REPLY
        this._parsed = null
        this._renderPanel()
      },
      () => this._toast("Copy failed — try again.")
    )
  }

  // Apply reply: parses the pasted AI reply and shows options
  applyReply(event) {
    event?.preventDefault?.()
    const textarea = this.panelBodyTarget.querySelector(".pb-reply-input")
    const text = textarea?.value || ""
    if (!text.trim()) {
      this._toast("Paste the AI reply first.")
      return
    }
    this._parsed = parseReply(text)
    this._renderPanel()
  }

  // Back to capture mode (discard unparsed reply)
  backToCapture(event) {
    event?.preventDefault?.()
    this._mode = MODE_CAPTURE
    this._parsed = null
    this._renderPanel()
  }

  // Use this option: write the revised text to plan_store at fieldPath
  useOption(event) {
    const id = event.currentTarget.dataset.pbId
    const optIndex = parseInt(event.currentTarget.dataset.pbOption, 10)
    if (!id || isNaN(optIndex)) return

    const item = this._items.find(it => it.id === id)
    if (!item) return

    const entry = this._parsed?.[id]
    const text = entry?.options?.[optIndex]
    if (text == null) return

    this._applyTextToField(item, text)
    this._markResolved(id)
  }

  // Keep original: mark item resolved without changing text
  keepOriginal(event) {
    const id = event.currentTarget.dataset.pbId
    if (!id) return
    this._markResolved(id)
  }

  // Bulk: keep all originals
  keepAllOriginals(event) {
    event?.preventDefault?.()
    for (const item of this._items) {
      if (item.status === "open") {
        this._markResolved(item.id)
      }
    }
  }

  // Bulk: use option 1 for all items that have it
  useOption1ForAll(event) {
    event?.preventDefault?.()
    for (const item of this._items) {
      if (item.status !== "open") continue
      const entry = this._parsed?.[item.id]
      const text = entry?.options?.[0]
      if (text != null) {
        this._applyTextToField(item, text)
        this._markResolved(item.id)
      }
    }
  }

  _applyTextToField(item, text) {
    if (!item.fieldPath) return

    // Write text back via plan_store merge
    const path = item.fieldPath.split(".")
    let update = {}
    let cursor = update
    for (let i = 0; i < path.length - 1; i++) {
      cursor[path[i]] = {}
      cursor = cursor[path[i]]
    }
    cursor[path[path.length - 1]] = text
    savePlan(update)

    // Also update the DOM element directly for immediate feedback
    const el = this._resolveField(item)
    if (el) {
      if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
        el.value = text
        el.dispatchEvent(new Event("input", { bubbles: true }))
      } else if (el.isContentEditable || el.getAttribute("contenteditable") === "true") {
        el.textContent = text
        el.dispatchEvent(new Event("input", { bubbles: true }))
      }
    }
  }

  _markResolved(id) {
    bucketUpdateStatus(id, "resolved")
    this._items = bucketLoad()
    this._removeOverlay(id)
    this._reflectBadge()
    this._renderPanel()
  }

  scrollToItem(event) {
    const id = event.currentTarget.dataset.pbId
    const entry = this._highlightMap.get(id)
    if (entry && entry.el) {
      entry.el.scrollIntoView({ behavior: "smooth", block: "center" })
      entry.el.focus?.()
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Find field from selection
  // ─────────────────────────────────────────────────────────────────────────

  _findField(sel) {
    if (!sel || sel.rangeCount === 0) return null
    const range = sel.getRangeAt(0)
    let container = range.commonAncestorContainer
    if (container.nodeType === Node.TEXT_NODE) container = container.parentNode

    // Find the editable element
    const el =
      container.closest?.("input, textarea, [contenteditable='true']") ||
      container.closest?.("[data-plan-field]")
    if (!el) return null

    return this._buildField(el, sel)
  }

  // Build the capture record for a known editable element. Split out of
  // _findField so the right-click menu can seed it from the event target
  // (an <input>/<textarea> selection isn't visible in window.getSelection()).
  _buildField(el, sel) {
    if (!el) return null
    const sectionEl = el.closest?.("[data-section-key]") || el.closest?.("[data-controller]")
    const sectionKey = sectionEl?.dataset?.sectionKey || this._inferSectionKey(el)
    const fieldPath = this._inferFieldPath(el)

    let text = ""
    let start = 0
    let end = 0

    if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
      start = el.selectionStart ?? 0
      end = el.selectionEnd ?? el.value.length
      text = el.value.slice(start, end)
      if (!text && el.value) { text = el.value; start = 0; end = el.value.length }
    } else if (el.isContentEditable || el.getAttribute("contenteditable") === "true") {
      text = sel ? sel.toString() : ""
      start = 0
      end = text.length
      if (!text && el.textContent) { text = el.textContent; start = 0; end = el.textContent.length }
    }

    if (!text) return null
    return { el, sectionKey, fieldPath, start, end, text }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Right-click "Add to Proofread bucket"
  // ─────────────────────────────────────────────────────────────────────────

  _handleContextMenu(e) {
    // The bucket toolbar button is admin-only; match its visibility so we don't
    // offer "Add to bucket" to users who can't open the panel.
    if (!this.hasToggleBtnTarget) return
    const el = e.target?.closest?.("input, textarea, [contenteditable='true'], [data-plan-field]")
    if (!el) return
    // Only take over the native menu when there's a REAL selection in the field.
    let hasSelection = false
    if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
      hasSelection = (el.selectionStart ?? 0) !== (el.selectionEnd ?? 0)
    } else {
      const s = window.getSelection()
      hasSelection = !!(s && !s.isCollapsed && String(s).trim() && el.contains(s.anchorNode))
    }
    if (!hasSelection) return
    const field = this._buildField(el, window.getSelection())
    if (!field || !field.text) return
    e.preventDefault()
    this._showContextMenu(e.clientX, e.clientY, field)
  }

  _showContextMenu(x, y, field) {
    this._removeContextMenu()
    const menu = document.createElement("div")
    menu.className = "pb-ctxmenu"
    menu.setAttribute("role", "menu")
    menu.innerHTML =
      `<button type="button" class="pb-ctxmenu-item" role="menuitem">` +
      `<i data-lucide="highlighter"></i><span>Add to Proofread bucket</span></button>`
    document.body.appendChild(menu)
    // Keep the menu inside the viewport.
    const rect = menu.getBoundingClientRect()
    menu.style.left = `${Math.max(4, Math.min(x, window.innerWidth - rect.width - 8))}px`
    menu.style.top = `${Math.max(4, Math.min(y, window.innerHeight - rect.height - 8))}px`
    renderIcons(menu)
    menu.querySelector(".pb-ctxmenu-item").addEventListener("click", () => {
      this._captureField(field)
      this._removeContextMenu()
    })
    this._ctxMenu = menu
    this._ctxDismiss = (ev) => {
      if (ev.type === "keydown" && ev.key !== "Escape") return
      if (ev.type === "mousedown" && menu.contains(ev.target)) return
      this._removeContextMenu()
    }
    // Defer so the current contextmenu event doesn't immediately dismiss it.
    setTimeout(() => {
      document.addEventListener("mousedown", this._ctxDismiss, true)
      document.addEventListener("keydown", this._ctxDismiss, true)
      window.addEventListener("scroll", this._ctxDismiss, true)
    }, 0)
  }

  _removeContextMenu() {
    if (this._ctxMenu) { this._ctxMenu.remove(); this._ctxMenu = null }
    if (this._ctxDismiss) {
      document.removeEventListener("mousedown", this._ctxDismiss, true)
      document.removeEventListener("keydown", this._ctxDismiss, true)
      window.removeEventListener("scroll", this._ctxDismiss, true)
      this._ctxDismiss = null
    }
  }

  _inferSectionKey(el) {
    // Try data attributes
    const dataField = el.dataset?.planField || ""
    if (dataField) {
      const parts = dataField.split(".")
      return parts[0] || ""
    }
    // Try common ancestor with controller
    const ctrl = el.closest?.("[data-controller]")
    if (ctrl) {
      const ctrls = ctrl.dataset?.controller || ""
      // Map controller names to section keys
      if (ctrls.includes("client-section")) return "client"
      if (ctrls.includes("dynamic-list")) {
        const path = ctrl.dataset?.dynamicListPathValue || ""
        return path.split(".")[0] || ""
      }
    }
    return ""
  }

  _inferFieldPath(el) {
    // Use data-plan-field if available
    if (el.dataset?.planField) return el.dataset.planField

    // Try data-field on the element or parent row
    const row = el.closest?.("[data-row]")
    if (row) {
      const rowId = row.dataset?.id || ""
      const field = el.dataset?.field || el.name || ""
      const listPath = el.closest?.("[data-dynamic-list-path-value]")?.dataset?.dynamicListPathValue || ""
      if (listPath && rowId) {
        return field ? `${listPath}.${rowId}.${field}` : `${listPath}.${rowId}`
      }
    }

    return el.dataset?.planField || el.name || ""
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Keyboard shortcut
  // ─────────────────────────────────────────────────────────────────────────

  _handleKeyDown(e) {
    // Ctrl+Shift+P (Windows) or Cmd+Shift+P (Mac)
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "p") {
      e.preventDefault()
      this.capture()
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Panel rendering
  // ─────────────────────────────────────────────────────────────────────────

  _renderPanel() {
    if (!this.hasPanelBodyTarget) return

    if (this._mode === MODE_REPLY) {
      this._renderReplyMode()
    } else {
      this._renderCaptureMode()
    }
  }

  _renderCaptureMode() {
    const items = this._items

    if (!items.length) {
      this.panelBodyTarget.innerHTML =
        '<p class="pb-empty">No items captured yet. Select text in a plan field and press <kbd>Ctrl+Shift+P</kbd> or click the Proofread button.</p>'
      return
    }

    // Group by section
    const grouped = new Map()
    for (const it of items) {
      const key = it.sectionKey || "other"
      if (!grouped.has(key)) grouped.set(key, [])
      grouped.get(key).push(it)
    }

    let html = '<div class="pb-actions">'
    html += '<button type="button" class="pb-action-btn" data-action="proofread-bucket#copyPrompt" title="Copy prompt to clipboard"><i data-lucide="clipboard-copy"></i> Copy prompt</button>'
    html += '<button type="button" class="pb-action-btn pb-action-btn--muted" data-action="proofread-bucket#clearResolved" title="Clear resolved items"><i data-lucide="trash-2"></i> Clear resolved</button>'
    html += '</div>'

    for (const [key, group] of grouped) {
      html += `<div class="pb-group">`
      html += `<div class="pb-group-head">${esc(sectionLabel(key))}</div>`
      for (const it of group) {
        const preview = it.original.slice(0, 60) + (it.original.length > 60 ? "…" : "")
        const statusClass = it.status === "resolved" ? " pb-item--resolved" : ""
        html += `
          <div class="pb-item${statusClass}" data-pb-id="${esc(it.id)}">
            <div class="pb-item-text" data-action="click->proofread-bucket#scrollToItem" data-pb-id="${esc(it.id)}" title="Click to scroll to field">${esc(preview)}</div>
            <div class="pb-item-meta">${esc(it.fieldPath)}</div>
            <button type="button" class="pb-item-remove" data-action="proofread-bucket#remove" data-pb-id="${esc(it.id)}" title="Remove"><i data-lucide="x"></i></button>
          </div>`
      }
      html += '</div>'
    }

    this.panelBodyTarget.innerHTML = html
    renderIcons(this.panelTarget)
  }

  _renderReplyMode() {
    const openItems = this._items.filter(it => it.status === "open")

    // If we have parsed results, show the review UI
    if (this._parsed) {
      this._renderReviewUI(openItems)
      return
    }

    // Otherwise show paste textarea
    let html = '<div class="pb-reply-section">'
    html += '<button type="button" class="pb-back-btn" data-action="proofread-bucket#backToCapture"><i data-lucide="arrow-left"></i> Back</button>'
    html += '<p class="pb-reply-hint">Paste the AI reply below:</p>'
    html += '<textarea class="pb-reply-input" placeholder="Paste the AI response here…" rows="8"></textarea>'
    html += '<button type="button" class="pb-action-btn" data-action="proofread-bucket#applyReply"><i data-lucide="check"></i> Apply</button>'
    html += '</div>'

    this.panelBodyTarget.innerHTML = html
    renderIcons(this.panelTarget)
  }

  _renderReviewUI(openItems) {
    let html = '<div class="pb-review-section">'
    html += '<button type="button" class="pb-back-btn" data-action="proofread-bucket#backToCapture"><i data-lucide="arrow-left"></i> Back</button>'

    // Bulk actions
    html += '<div class="pb-bulk-actions">'
    html += '<button type="button" class="pb-action-btn pb-action-btn--muted" data-action="proofread-bucket#keepAllOriginals"><i data-lucide="x"></i> Keep all originals</button>'
    html += '<button type="button" class="pb-action-btn" data-action="proofread-bucket#useOption1ForAll"><i data-lucide="check"></i> Use option 1 for all</button>'
    html += '</div>'

    // Render each item with its options
    for (const item of openItems) {
      const entry = this._parsed[item.id]
      const options = entry?.options || []

      html += `<div class="pb-review-item" data-pb-id="${esc(item.id)}">`
      html += `<div class="pb-review-header">`
      html += `<span class="pb-review-breadcrumb">${esc(sectionLabel(item.sectionKey))} › ${esc(item.fieldPath)}</span>`
      html += `</div>`

      // Original text
      html += `<div class="pb-review-original">`
      html += `<div class="pb-review-label">Original</div>`
      html += `<div class="pb-review-text">${esc(item.original)}</div>`
      html += `<button type="button" class="pb-option-btn pb-option-btn--keep" data-action="proofread-bucket#keepOriginal" data-pb-id="${esc(item.id)}"><i data-lucide="x"></i> Keep original</button>`
      html += `</div>`

      if (options.length === 0) {
        html += `<div class="pb-no-suggestion">No suggestion returned for this item.</div>`
      } else {
        // Render each option with diff
        for (let i = 0; i < options.length; i++) {
          const opt = options[i]
          const diffHtml = this._renderDiff(item.original, opt)

          html += `<div class="pb-option-card">`
          html += `<div class="pb-option-label">Option ${i + 1}</div>`
          html += `<div class="pb-option-diff">${diffHtml}</div>`
          html += `<button type="button" class="pb-option-btn pb-option-btn--use" data-action="proofread-bucket#useOption" data-pb-id="${esc(item.id)}" data-pb-option="${i}"><i data-lucide="check"></i> Use this</button>`
          html += `</div>`
        }
      }

      html += `</div>` // .pb-review-item
    }

    html += '</div>' // .pb-review-section

    this.panelBodyTarget.innerHTML = html
    renderIcons(this.panelTarget)
  }

  _renderDiff(original, revised) {
    const spans = wordDiff(original, revised)
    let html = ""
    for (const span of spans) {
      const text = esc(span.text)
      if (span.type === "delete") {
        html += `<span class="pb-diff-del">${text}</span>`
      } else if (span.type === "insert") {
        html += `<span class="pb-diff-ins">${text}</span>`
      } else {
        html += text
      }
    }
    return html
  }

  _showPanel() {
    if (!this.hasPanelTarget) return
    this.panelTarget.hidden = false
    requestAnimationFrame(() => this.panelTarget.classList.add("is-open"))
  }

  _hidePanel() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("is-open")
    this.panelTarget.hidden = true
  }

  _reflectBadge() {
    if (!this.hasBadgeTarget) return
    const count = this._items.filter((it) => it.status === "open").length
    this.badgeTarget.textContent = String(count)
    this.badgeTarget.hidden = count === 0
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Highlighting
  // ─────────────────────────────────────────────────────────────────────────

  _scheduleHighlights() {
    if (this._highlightRaf) cancelAnimationFrame(this._highlightRaf)
    this._highlightRaf = requestAnimationFrame(() => {
      this._highlightRaf = null
      this._reapplyHighlights()
    })
  }

  _reapplyHighlights() {
    // Remove all existing overlays and reapply from current items
    this._removeAllOverlays()
    for (const item of this._items) {
      const el = this._resolveField(item)
      if (el) this._applyHighlight(item, el)
    }
  }

  _applyHighlight(item, el) {
    if (!el || this._highlightMap.has(item.id)) return

    if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
      // For input/textarea, add a data attribute and CSS class
      el.classList.add("pb-marked")
      el.dataset.pbItemId = item.id
      this._highlightMap.set(item.id, { el, overlay: null })
    } else if (el.isContentEditable || el.getAttribute("contenteditable") === "true") {
      // For contenteditable, wrap selection in <mark> if possible
      // This is complex; for now just add a class
      el.classList.add("pb-marked")
      el.dataset.pbItemId = item.id
      this._highlightMap.set(item.id, { el, overlay: null })
    }
  }

  _removeOverlay(id) {
    const entry = this._highlightMap.get(id)
    if (!entry) return
    if (entry.el) {
      entry.el.classList.remove("pb-marked")
      delete entry.el.dataset.pbItemId
    }
    this._highlightMap.delete(id)
  }

  _removeAllOverlays() {
    for (const [id] of this._highlightMap) {
      this._removeOverlay(id)
    }
    this._highlightMap.clear()
  }

  _resolveField(item) {
    if (!item.fieldPath) return null
    const path = item.fieldPath

    // Escape for attribute selector (don't escape dots in the value)
    const a = (s) => String(s).replace(/["\\]/g, "\\$&")
    const q = (sel) => { try { return document.querySelector(sel) } catch (_) { return null } }

    // 1) plan-store field: the whole dotted path is the attribute value.
    let el = q(`[data-plan-field="${a(path)}"]`)
    if (el) return el

    // 2) dynamic-list: try to find by path components
    const parts = path.split(".")
    if (parts.length >= 2) {
      const section = parts.slice(0, -1).join(".")
      const field = parts[parts.length - 1]
      el = q(`[data-dynamic-list-path-value="${a(section)}"] [data-field="${a(field)}"]`)
      if (el) return el
    }

    return null
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Toast
  // ─────────────────────────────────────────────────────────────────────────

  _toast(msg) {
    let bar = document.getElementById("pb-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "pb-toast"
      bar.className = "pb-toast"
      document.body.appendChild(bar)
    }
    bar.textContent = msg
    bar.classList.add("is-visible")
    clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => bar.classList.remove("is-visible"), 2500)
  }
}
